import { GenericTable } from "@shared/components/GenericTable";
import { StepHelpers } from "@shared/components/Stepper";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Separator } from "@shared/ui/separator";
import { Download, Info, Printer } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@shared/hooks/use-toast";
import {
  useApproveBill,
  useBillDetails,
  useDownloadBill,
  usePrintBill,
  useViewBill,
} from "../../hooks";
import ReadingStatusBadge from "./ReadingStatusBadge";
import { logEvent } from "@shared/analytics/analytics";

interface Step4BillReviewProps {
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  // Additional stepper props
  onComplete?: (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => void;
  allStepData?: Record<number, any>;
}

const Step4BillReview: React.FC<Step4BillReviewProps> = ({
  stepHelpers,
  currentStepIndex = 3,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
  onComplete,
  allStepData,
}) => {
  const { id, draftId, action = "add" } = useParams();

  const [sendNotification, setSendNotification] = useState(true);
  const dataLoadedRef = useRef(false);
  const navigate = useNavigate();
  const CURRENCY = useCurrency();

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Step 4 data saved:", stepData);
      } catch (error) {
        console.error("Failed to save step 4 data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step 4 data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to get step 3 data (readings & consumption)
  const getStep3Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(2); // Step 3 is at index 2
    } catch (error) {
      console.error("Failed to get step 3 data:", error);
      return null;
    }
  }, [stepHelpers]);

  // Helper function to get step 1 data (consumer selection)
  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(0); // Step 1 is at index 0
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers]);

  // Get the finalBillId from step 3 data
  const step3Data = getStep3Data();
  const step1Data = getStep1Data();

  const billId = step3Data?.finalBillId || id;

  const billDraftId = draftId || step1Data?.billDraftId;

  // API hooks
  const {
    data: billDetails,
    isLoading: isBillDetailsLoading,
    isError: isBillDetailsError,
  } = useBillDetails(billId);

  const { mutateAsync: approveBill, isPending: isApprovingBill } =
    useApproveBill(billId, billDraftId);

  const printBill = usePrintBill();
  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          console.log("Loaded step 4 data:", savedStepData);

          // Restore notification preference
          setSendNotification(savedStepData.sendNotification ?? true);
        }

        // Save initial step data with bill ID
        saveStepData({
          billId,
          finalBillId: step3Data?.finalBillId,
          billDraftId: step1Data?.billDraftId,
          sendNotification: savedStepData?.sendNotification ?? true,
          initialLoadAt: new Date().toISOString(),
        });

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step 4 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    getStepData,
    // saveStepData,
    billId,
    step3Data,
    step1Data,
  ]);

  // Auto-save when notification setting changes
  useEffect(() => {
    if (dataLoadedRef.current) {
      saveStepData({
        billId,
        sendNotification,
        billDetails: billDetails
          ? {
              billNumber: billDetails.billNumber,
              consumerName: billDetails.consumerName,
              consumerNumber: billDetails.consumerNumber,
              billDate: billDetails.billDate,
              dueDate: billDetails.dueDate,
              totalAmountDue: billDetails.totalAmountDue,
              status: billDetails.status,
              utilityService: billDetails.utilityService,
            }
          : null,
        lastUpdated: new Date().toISOString(),
      });
    }
  }, [sendNotification, billDetails, billId]);

  // Handle bill approval and completion
  const handleComplete = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    if (!billId) {
      if (setValidationError) {
        setValidationError(
          "No bill ID found. Please go back and regenerate the bill."
        );
      }
      return;
    }

    try {
      const response = await approveBill(sendNotification, {
        onSuccess: () => {
          logEvent("Bill Approved & Completed", {
            timestamp: Date.now(),
          });
        },
      });

      if (response) {
        stepHelpers?.resetAllData();

        navigate(`/bx/search?bill_run_id=${billId}`);

        toast({
          title: "Success",
          description: "Bill approved and generated successfully!",
        });
      } else {
        if (setValidationError) {
          setValidationError("Failed to approve bill. Please try again.");
        }
      }
    } catch (error) {
      console.error("Failed to approve bill:", error);
      if (setValidationError) {
        setValidationError(
          "An error occurred while approving the bill. Please try again."
        );
      }
    }
  };

  const handleDraft = async () => {
    navigate("/bx/history?tab=single&tab1=draft");
  };

  const handlePrevious = () => {
    // Save current state before going back
    saveStepData({
      billId,
      sendNotification,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    });

    if (onPrevious) {
      onPrevious();
    }
  };

  // Handle bill actions
  const handleDownloadClick = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      toast({
        title: "Success",
        description: "Bill downloaded successfully",
        variant: "default",
      });
      // Save download action
      saveStepData({
        billId,
        sendNotification,
        lastAction: {
          type: "download",
          timestamp: new Date().toISOString(),
          filename: filename || `bill-${billId}.pdf`,
        },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download the bill",
        variant: "destructive", // or "destructive" if you want the default red
      });
    }
  };

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);

      // Save view action
      saveStepData({
        billId,
        sendNotification,
        lastAction: {
          type: "view",
          timestamp: new Date().toISOString(),
        },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to view the bill",
        variant: "destructive", // or "destructive" if you want the default red
      });
    }
  };

  const handlePrintClick = async (billId: string) => {
    try {
      await printBill.mutateAsync(billId);

      // Save print action
      saveStepData({
        billId,
        sendNotification,
        lastAction: {
          type: "print",
          timestamp: new Date().toISOString(),
        },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to print the bill",
        variant: "destructive", // or "destructive" if you want the default red
      });

    }
  };

  // Handle notification setting change
  const handleNotificationChange = useCallback((checked: boolean) => {
    setSendNotification(checked);
  }, []);

  // Table columns for meter readings
  const meterReadingColumns: TableColumn[] = [
    {
      key: "utilityService",
      header: "Utility Type",
    },
    {
      key: "meterInfo",
      header: "Meter No / Device No",
      render: (_, row) => (
        <div>
          <div className="px-3 py-2 font-semibold">
            {row.meterNumber ?? "NA"}/{row.deviceNo ?? "NA"}
          </div>
        </div>
      ),
    },
    {
      key: "meterType",
      header: "Meter Type",
    },
    {
      key: "previousReading",
      header: "Previous Reading",
      width: "102px",
      render: (_, row) => (
        <div>
          <div>{row.lastReading}</div>
          <div className="px-3 py-2 font-semibold">
            {row.previousReadingDate ?? "NA"}
          </div>
        </div>
      ),
    },
    {
      key: "previousReadingMeterStatus",
      header: "Previous Status",
      width: "90px",
      render: (value) => <ReadingStatusBadge status={value ?? "NA"} />,
    },
    {
      key: "currentReading",
      header: "Current Reading",
      width: "105px",
      render: (_, row) => (
        <div>
          <div>{row.currentReading ?? "NA"}</div>
          <div className="text-xs text-muted-foreground">
            {row.currentReadingDate}
          </div>
        </div>
      ),
    },
    {
      key: "currentReadingMeterStatus",
      header: "Current Status",
      width: "90px",
      render: (value) => <ReadingStatusBadge status={value ?? "NA"} />,
    },
    {
      key: "totalConsumption",
      header: "Consumption (Units)",
      width: "115px",
      render: (value) => <span className="font-semibold">{value ?? "NA"}</span>,
    },
  ];

  // Show loading state if critical data is not available
  if (isBillDetailsLoading || !billId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bill details...</p>
        </div>
      </div>
    );
  }

  // Show error state if critical data failed to load
  if (isBillDetailsError || !billDetails) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load bill details.</p>
          <div className="space-x-2">
            <Button onClick={handlePrevious} variant="outline">
              Go Back
            </Button>
            <Button onClick={() => window.location.reload()} variant="default">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600">
          Review the generated bill details and approve to complete the process.
        </p>
      </div>

      <Card className="border bg-card shadow-none mb-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-bold">Bill Summary</CardTitle>
          <CardDescription>
            Review and generate the bill. Please verify all details before
            approval.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Three information cards at the top: Consumer Info, Bill Details, Charge Breakdown */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Consumer Info */}
        <Card className="flex-1 min-w-[240px] px-0 py-0">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-base font-semibold">
              Consumer Info
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3 px-4">
            <div className="space-y-1.5 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">Name:</span>{" "}
                {billDetails?.consumerName}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">
                  Account No.:
                </span>{" "}
                {billDetails?.consumerNumber}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">
                  Utilities:
                </span>{" "}
                {billDetails?.utilityService}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1 min-w-[240px] px-0 py-0">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-base font-semibold">
              Bill Details
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3 px-4">
            <div className="space-y-1.5 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">
                  Bill No.:
                </span>{" "}
                {billDetails?.billNumber}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">
                  Issue Date:
                </span>{" "}
                {billDetails?.billDate}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">
                  Due Date:
                </span>{" "}
                {billDetails?.dueDate}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">
                  Bill Amount:
                </span>{" "}
                {billDetails?.totalAmountDue}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">
                  Status:
                </span>{" "}
                <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                  {billDetails?.status}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charges Breakdown */}
        <Card className="flex-1 min-w-[260px] px-0 py-0">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-base font-semibold">
              Charges Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3 px-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {[
                    {
                      name: "Outstanding Balance",
                      key: "arrears",
                      value: billDetails?.totalOutstandingBalance || 0,
                    },
                    {
                      name: "Late Fees",
                      key: "lateFee",
                      value: billDetails?.lateFees || 0,
                    },
                    {
                      name: "Discounts/Subsidies",
                      key: "discounts",
                      value: billDetails?.discounts || 0,
                    },
                    {
                      name: "Service Charges",
                      key: "serviceCharges",
                      value: 0,
                    },
                  ].map((row) => (
                    <tr key={row.key}>
                      <td className="py-1 pr-4 font-medium">{row.name}</td>
                      <td className="py-1 pr-2 text-right whitespace-nowrap">
                        {row.value >= 0 ? `${CURRENCY}` : `-${CURRENCY}`}{" "}
                        {Math.abs(row.value).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notification options moved up and compacted */}
      <Card className="bg-muted/40 border-0 shadow-none mb-0 mt-2">
        <CardHeader className="pb-0 pt-3 px-4">
          <CardTitle className="text-base font-semibold">
            Notification Options
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sendNotification"
              checked={sendNotification}
              onCheckedChange={handleNotificationChange}
            />
            <Label
              htmlFor="sendNotification"
              className="text-sm cursor-pointer"
            >
              Send Bill Notification
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Amount Due and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between mb-2">
            <div className="text-lg font-bold">Amount Due</div>
            <div className="text-2xl font-bold text-primary">
              {CURRENCY} {billDetails?.totalAmountDue}
            </div>
          </div>
          <Separator />
          <div className="flex flex-wrap gap-1 mt-3 items-center justify-end">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="default"
                onClick={() => handleViewClick(billId!)}
                disabled={viewBill.isPending}
              >
                <span className="flex items-center gap-1">
                  <Info className="h-4 w-4" />
                  {viewBill.isPending ? "Loading..." : "Preview"}
                </span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDownloadClick(billId!)}
                disabled={downloadBill.isPending}
              >
                <span className="flex items-center gap-1">
                  <Download className="h-4 w-4" />
                  {downloadBill.isPending ? "Downloading..." : "Download"}
                </span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePrintClick(billId!)}
                disabled={printBill.isPending}
              >
                <span className="flex items-center gap-1">
                  <Printer className="h-4 w-4" />
                  {printBill.isPending ? "Printing..." : "Print"}
                </span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meter Readings - now below top cards, with full width and clearer columns */}
      <Card className="w-full shadow-none mt-0">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-base font-semibold">
            Meter Readings
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3 px-4 overflow-x-auto">
          <GenericTable
            data={billDetails?.meterReadingDetails ?? []}
            columns={meterReadingColumns}
            rowKey="deviceNo"
            emptyMessage="No meter readings found"
          />
        </CardContent>
      </Card>

      {/* Final Action Buttons */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isApprovingBill || isValidating}
        >
          Previous
        </Button>
        <div>
          <Button onClick={handleDraft} className="min-w-[200px] mr-5">
            Save As Draft
          </Button>
          <Button
            onClick={handleComplete}
            disabled={isApprovingBill || isValidating}
            className="min-w-[200px] bg-green-600 hover:bg-green-700"
          >
            {isApprovingBill || isValidating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isApprovingBill ? "Approving Bill..." : "Validating..."}
              </>
            ) : (
              "Approve & Complete"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step4BillReview;
